import React, { FunctionComponent, useState, useCallback, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';

import Button from '../../../UI/Button';
import Modal from '../../../UI/Modal';
import Icon from '../../../Icons';
import { fetchTransactions } from '../actions';
import InfinityScroll from '../../../InfinityScroll';
import useInfiniteScroll from '../../../InfinityScroll/scrollHook';
import { dataMapping } from './dataMapping';
import DatePick from '../../../UI/Date';
import { modifyTransactionDate } from '../../helper';
import { downloadTransactions } from '../../download';
import UrlConfig from '../../../../utils/urlHelper';

import { Container, DateContainer, ModalTitle, ButtonList, DoubleIconButton } from './styles';
import { sendEvent2 } from '../../../../utils/tracking';

interface Url {
  downloadPassbook: Function;
}

const urlHelper: Url = UrlConfig;

const Transactions: FunctionComponent = memo(() => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(0);
  const [dates, setDates] = useState({});
  const [error, setError] = useState('');
  const [downloadModalState, setDownloadModalState] = useState(false);

  const transactions = useSelector((state: any) => _get(state, 'passbook.transactionData.list', []));
  const totalTransactions = useSelector((state: any) => _get(state, 'passbook.transactionData.totalCount', 0));
  const totalPages = useSelector((state: any) => _get(state, 'passbook.transactionData.totalPages', 0));
  const isLoaded: boolean = useSelector((state: any) => state.passbook.loaded);

  const dispatchToStore = useCallback((params: any) => dispatch(fetchTransactions(params)), [dispatch]);

  useEffect(() => {
    dispatchToStore({ pageNo: page, reset: false });
    setIsFetching(false);
    // TODO: handle dependencies page, setIsFetching
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchToStore]);

  const fetchMoreTransactions = useCallback(() => {
    if (transactions.length < totalTransactions && page + 1 < totalPages) {
      const { startDate = '', endDate = '' } = dates as any;
      const pageNo = page + 1;
      setPage((prevState) => pageNo);
      dispatchToStore({
        pageNo,
        fromTime: startDate,
        toTime: endDate,
        reset: false,
        fetchMore: true,
      });
    }
    setIsFetching(false);
  }, [transactions, totalTransactions, page, totalPages, setPage, dispatchToStore]);

  const [isFetching, setIsFetching] = useInfiniteScroll(fetchMoreTransactions);

  const toggleDownloadModal = () => {
    if (!downloadModalState) {
      sendEvent2(
        'pop_up_view',
        {
          event_action: 'view',
          event_category: 'download_report_pop_up',
          screen_name: 'ft_passbook',
          target_product: 'fastag',
          event_platform: 'web',
        },
        true,
      );
    }
    setDownloadModalState((prevState) => !prevState);
  };

  const closeDownloadModal = () => {
    sendEvent2(
      'close_btn',
      {
        event_action: 'click',
        event_category: 'download_report_pop_up',
        screen_name: 'ft_passbook',
        target_product: 'fastag',
        event_platform: 'web',
      },
      true,
    );
    setDownloadModalState(false);
  };

  const downloadReport = (reportFormat: 'xlsx' | 'pdf') => {
    sendEvent2(
      reportFormat === 'xlsx' ? 'download_excel_btn' : 'download_pdf_btn',
      {
        event_action: 'click',
        event_category: 'download_report_pop_up',
        screen_name: 'ft_passbook',
        target_product: 'fastag',
        event_platform: 'web',
      },
      true,
    );
    let { startDate = '', endDate = '' } = dates as any;
    // If end date is not selected, setting default value of today
    if (!endDate) {
      endDate = Math.round(Date.now() / 1000);
    }
    // If start date is not selected, setting default value of 30 days prior to end date
    if (!startDate) {
      startDate = endDate - 2592000;
    }
    downloadTransactions(
      {
        fromTime: startDate,
        toTime: endDate,
        reportFormat: reportFormat,
      },
      urlHelper.downloadPassbook,
    );
    closeDownloadModal();
  };

  const dataList: any = dataMapping({});

  const validateDate = (label: string, value: Date) => {
    const dateObj = modifyTransactionDate(label, value, dates, true);
    setDates(dateObj);
    const { startDate, endDate } = dateObj;
    if (startDate && endDate) {
      const diff = endDate - startDate;
      if (diff < 0) {
        setError('Start Date cannot be greater that End Date');
      } else {
        setError('');
        setPage(0);
        dispatchToStore({
          pageNo: 0,
          fromTime: startDate,
          toTime: endDate,
          reset: true,
        });
      }
    }
  };

  return (
    <Container>
      <div className="flex justify-content-s summary falign-c bg-white relative">
        <div className="">
          <span className="font-light-grey">Transactions :</span>
          <span className="fwb ml5">{totalTransactions || '---'}</span>
        </div>
        <div className="flex falign-c">
          <div className="mr16">
            <Button
              variant="outlined"
              icon="download"
              clickHandler={() => {
                sendEvent2(
                  'download_statement',
                  {
                    event_action: 'click',
                    event_category: 'top_nav',
                    screen_name: 'ft_passbook',
                    target_product: 'fastag',
                    event_platform: 'web',
                  },
                  true,
                );
                toggleDownloadModal();
              }}
            >
              Download Statement
            </Button>
          </div>
          <span className="ml16 mr16 font-greyish none">|</span>
          <DateContainer className="flex relative falign-c">
            <div className="relative">
              <DatePick
                name="startDate"
                timePicker={true}
                placeholder="Start Date"
                value={null}
                changeHandler={(val: Date) => {
                  sendEvent2(
                    'start_date_btn',
                    {
                      event_action: 'click',
                      event_category: 'top_nav',
                      screen_name: 'ft_passbook',
                      target_product: 'fastag',
                      event_platform: 'web',
                    },
                    true,
                  );
                  validateDate('startDate', val);
                }}
              />
            </div>
            <span className="ml8 mr8 small">to</span>
            <div className="relative">
              <DatePick
                name="endDate"
                timePicker={true}
                placeholder="End Date"
                value={null}
                changeHandler={(val: Date) => {
                  sendEvent2(
                    'end_date_btn',
                    {
                      event_action: 'click',
                      event_category: 'top_nav',
                      screen_name: 'ft_passbook',
                      target_product: 'fastag',
                      event_platform: 'web',
                    },
                    true,
                  );
                  validateDate('endDate', val);
                }}
              />
            </div>
          </DateContainer>
        </div>
        <p className={`smaller b0 error absolute r30 ${error ? '' : 'hide'}`}>{error}</p>
      </div>
      <div>
        <InfinityScroll componentList={dataList} componentData={transactions} loading={isLoaded} />
      </div>
      <Modal isOpen={downloadModalState} onClose={closeDownloadModal}>
        <div className="flex flex-dir-c falign-s">
          <ModalTitle>Select download format for Passbook.</ModalTitle>
          <ButtonList>
            <DoubleIconButton onClick={() => downloadReport('xlsx')}>
              <div className="left">
                <Icon name="xls" />
                Excel Format
              </div>
              <div className="right">
                <Icon name="download-blue" />
              </div>
            </DoubleIconButton>
            <DoubleIconButton onClick={() => downloadReport('pdf')}>
              <div className="left">
                <Icon name="pdf" />
                PDF Format
              </div>
              <div className="right">
                <Icon name="download-blue" />
              </div>
            </DoubleIconButton>
          </ButtonList>
        </div>
      </Modal>
    </Container>
  );
});

export default Transactions;
